/**
 * CompanyRailSidebar
 *
 * Thin wrapper around `RailSidebar` that injects the company-scope
 * domain definitions and switches the visual variant to `'company'`.
 *
 * Visual differentiation from the location rail (same UX shape — rail
 * + panel — so the user's muscle memory carries over):
 *   - Active accent swaps coral → ocean to match the scope band on the
 *     left edge of <main>.
 *   - Domain monograms are 'OP / PE / FI / AD' (different from
 *     location's 'FD / CO / TM / TR / ST').
 *   - Same panel-header treatment, same collapsible behavior.
 *
 * Builds `CompanyGateFlags` from RBAC helpers and passes the result
 * into `buildCompanyDomains()`. Permissions left out of the flag set
 * fall back to "show" — finer-grained gating happens at the route
 * guard level.
 */
import { useMemo } from 'react';

import { useTranslation } from '@/core';
import { useAuth } from '@/core/auth/useAuth';
import {
  canManageCompanyIntegrations,
  canManageCompanyLocations,
  canManageCompanyUsers,
  canViewPlatformInvoices,
  canViewStaffModule,
} from '@/core/auth/modulePermissions';
import { canViewCompensation } from '@/modules/hr/utils/compensationVisibility';
import { canViewTreasury } from '@/modules/treasury/utils/permissions';

import { RailSidebar } from './RailSidebar';
import { buildCompanyDomains, type CompanyGateFlags } from './companyNavConfig';

export function CompanyRailSidebar() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const gates: CompanyGateFlags = useMemo(
    () => ({
      canSeeCompanyUsers: canManageCompanyUsers(user),
      canSeeCompanyLocations: canManageCompanyLocations(user),
      canSeeCompanyIntegrations: canManageCompanyIntegrations(user),
      canSeeStaff: canViewStaffModule(user, null),
      canSeePayroll: canViewCompensation(user, null),
      canSeeTreasury: canViewTreasury(user, 'company', null),
      canSeePlatformInvoices: canViewPlatformInvoices(user),
    }),
    [user],
  );

  const domains = useMemo(() => buildCompanyDomains({ t, gates }), [t, gates]);

  return <RailSidebar variant="company" domains={domains} />;
}
